/**
 * Where a body paragraph sits inside the document's tables, as the web reader
 * route reports it to the core and the core shows it to the edit model.
 *
 * A paragraph outside every table has an empty path. A paragraph inside a
 * table cell has one entry per enclosing cell, outermost first, so a
 * paragraph in a nested table carries two entries: the outer cell, then the
 * inner one. The last entry is the cell the paragraph is written in, and the
 * cell a row operation acts on.
 *
 * Every number is 1-based, because the model reads these numbers and quotes
 * them back, and every re-ask message counts the same way.
 */
import { z } from "zod";

/** The three states of `w:tcPr/w:vMerge`: absent, opening a merged group, or continuing the one above. */
export const verticalMergeStateSchema = z.enum(["none", "restart", "continue"]);
export type VerticalMergeState = z.infer<typeof verticalMergeStateSchema>;

/** Whether the row itself is a tracked insertion (`w:trPr/w:ins`) or a tracked deletion (`w:trPr/w:del`). */
export const tableRowRevisionStateSchema = z.enum(["none", "inserted", "deleted"]);
export type TableRowRevisionState = z.infer<typeof tableRowRevisionStateSchema>;

export const documentTableCellSchema = z.object({
    /** 1-based, in document order over every table of the body; a nested table has its own number. */
    tableNumber: z.number().int().positive(),
    /** 1-based row position among the table's rows; wrappers around `w:tr` do not count. */
    rowNumber: z.number().int().positive(),
    rowCount: z.number().int().positive(),
    /** 1-based cell position among the row's cells; a spanned cell is one cell, and a cell inside a content control counts once. */
    cellNumber: z.number().int().positive(),
    cellCount: z.number().int().positive(),
    /** 1-based grid columns the cell covers, `gridColumnFirst` through `gridColumnLast` inclusive, out of `gridColumnCount`. */
    gridColumnFirst: z.number().int().positive(),
    gridColumnLast: z.number().int().positive(),
    gridColumnCount: z.number().int().positive(),
    /** True for a row Word repeats on every page (`w:trPr/w:tblHeader`), and for the first row when the table's look says it is a header. */
    isHeaderRow: z.boolean(),
    /** The text of the header-row cell covering this cell's first grid column; null for a header-row cell, and when no header row has text there. */
    columnHeading: z.string().nullable(),
    verticalMerge: verticalMergeStateSchema,
    rowRevision: tableRowRevisionStateSchema,
});

export type DocumentTableCell = z.infer<typeof documentTableCellSchema>;

export const tableCellPathSchema = z.array(documentTableCellSchema);

export type TableCellPath = z.infer<typeof tableCellPathSchema>;
